import Link from "next/link"
import type { ViewState } from "../lib/routes.ts"
import { statePanelPresentations } from "../lib/state-copy.ts"
import { Icon } from "./icons.tsx"
import { Illustration } from "./illustration.tsx"

const maskedReference = (reference: string): string => `••••-${reference.slice(-4)}`
const assertNever = (value: never): never => {
  throw new TypeError(`UNSUPPORTED_STATE_ACTION:${JSON.stringify(value)}`)
}

export type StatePanelSurface = "financial" | "view"

export const StatePanel = ({ description, pageHeading, reference, state, surface = "financial" }: { readonly description: string; readonly pageHeading: string; readonly reference?: string; readonly state: Exclude<ViewState, "DEFAULT">; readonly surface?: StatePanelSurface }) => {
  const presentation = statePanelPresentations[state]
  const actions = presentation.actions.filter((action) => surface === "view" || action.kind !== "reload")
  return (
    <>
      <h1 className="state-route-heading">{pageHeading}</h1>
      <output aria-busy={state === "LOADING"} aria-live="polite" className={`state-panel state-${state.toLowerCase().replaceAll("_", "-")}`}>
        {presentation.showsRecoveryArtwork ? <Illustration decorative kind="recovery" /> : null}
        <span className="state-icon" data-state-icon={presentation.icon}>
          <Icon name={presentation.icon} />
        </span>
        <h2>{presentation.heading}</h2>
        <p className="phrase-safe-block">{description}</p>
        {reference === undefined ? null : (
          <dl className="detail-grid">
            <div>
              <dt>거래 확인 번호</dt>
              <dd>{maskedReference(reference)}</dd>
            </div>
          </dl>
        )}
        {presentation.showsSkeleton ? (
          <div aria-hidden="true" className="skeleton">
            <span />
            <span />
            <span />
          </div>
        ) : null}
        {actions.length === 0 ? null : (
          <div className="action-stack">
            {actions.map((action) => {
              switch (action.kind) {
                case "link":
                  return (
                    <Link className={action.href === "/balance/history" ? "button primary state-action" : "button secondary state-action"} data-state-action={action.kind} href={action.href} key={action.href}>
                      <Icon name={action.icon} />
                      <span>{action.label}</span>
                    </Link>
                  )
                case "reload":
                  return (
                    <button className="button secondary state-action" data-state-action={action.kind} key={action.kind} onClick={() => window.location.reload()} type="button">
                      <Icon name={action.icon} />
                      <span>{action.label}</span>
                    </button>
                  )
                default:
                  return assertNever(action)
              }
            })}
          </div>
        )}
      </output>
    </>
  )
}
